'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';

// Only the selected source changes. The real img URL, alt, dimensions and srcset
// stay in SSR HTML; browsers without scripting-media support use that img normally.
const PLACEHOLDER =
  `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1"/>')}`;
const pendingImages = new Map<Element, () => void>();
let observer: IntersectionObserver | undefined;

function forgetImage(image: Element) {
  pendingImages.delete(image);
  observer?.unobserve(image);
  if (pendingImages.size === 0) {
    observer?.disconnect();
    observer = undefined;
  }
}

function observeImage(image: Element, release: () => void) {
  if (typeof IntersectionObserver === 'undefined') {
    release();
    return;
  }

  try {
    observer ??= new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting || !entry.intersectionRect.width || !entry.intersectionRect.height) {
            continue;
          }
          const reveal = pendingImages.get(entry.target);
          if (!reveal) continue;
          forgetImage(entry.target);
          reveal();
        }
      },
      // Do not expand horizontally: hidden carousel slides must stay deferred.
      { rootMargin: '200px 0px', threshold: 0 },
    );
    pendingImages.set(image, release);
    observer.observe(image);
    return () => forgetImage(image);
  } catch {
    forgetImage(image);
    release();
  }
}

export function DeferredImagePicture({
  children,
  media = '(scripting: enabled)',
}: {
  children: ReactNode;
  media?: string;
}) {
  const pictureRef = useRef<HTMLPictureElement>(null);
  const [released, setReleased] = useState(false);

  useEffect(() => {
    const image = pictureRef.current?.querySelector('img');
    if (!image) {
      setReleased(true);
      return;
    }
    return observeImage(image, () => setReleased(true));
  }, []);

  return (
    <picture ref={pictureRef} style={{ display: 'contents' }}>
      {!released && <source media={media} srcSet={PLACEHOLDER} />}
      {children}
    </picture>
  );
}
